import { useEffect, useMemo, useState } from 'react'

import { useApolloClient, useQuery } from '@apollo/client'
import { Box, Title } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Add } from '@factorialco/f0-react/icons/app'
import {
  BenefitsEmployeesAllIdsDocument,
  BenefitsEmployeesFilterCompanyLegalEntitiesDocument,
  BenefitsEmployeesFilterManagersDocument,
  BenefitsEmployeesFilterTeamNamesDocument,
  BenefitsEmployeesPageDataCollectionDocument,
  BenefitsEmployeesPageDataCollectionQuery,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { buildUrl } from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import ActivateBenefitsModal, { BenefitsEmployeeActivation } from './ActivateBenefitsModal'
import { ActivationResultSidePanel } from './ActivationResultSidePanel'
import { CalloutResults } from './CalloutResults'

const translateBenefitType = (benefitType: string): string => {
  switch (benefitType.toLowerCase()) {
    case 'meals':
      return i18n.t('benefits.tabs.employees.page.table.benefits.meals')
    case 'transportation':
      return i18n.t('benefits.tabs.employees.page.table.benefits.transport')
    case 'childcare':
      return i18n.t('benefits.tabs.employees.page.table.benefits.childcare')
    case 'training':
      return i18n.t('benefits.tabs.employees.page.table.benefits.training')
    case 'wellness':
      return i18n.t('benefits.tabs.employees.page.table.benefits.wellness')
    case 'healthcare':
      return i18n.t('benefits.tabs.employees.page.table.benefits.health_insurance')
    default:
      return benefitType
  }
}

type OnActivationResultFn = React.ComponentProps<typeof ActivateBenefitsModal>['onActivationResult']

const EmployeesPage = () => {
  const {
    company: { id: companyId },
  } = useCurrentSession()

  const [sidepanelState, setSidepanelState] = useState(false)
  const [activationData, setActivationData] = useState<BenefitsEmployeeActivation | undefined>(
    undefined
  )

  const [legalEntities, setLegalEntities] = useState<Array<{ id: string; legalName: string }>>([])
  const [showWizardModal, setShowWizardModal] = useState<boolean>(false)

  const client = useApolloClient()

  const { data: allEmployeesWithBenefitsData } = useQuery(BenefitsEmployeesAllIdsDocument, {
    fetchPolicy: 'cache-and-network',
  })

  const employeesWithBenefits = useMemo(() => {
    if (!allEmployeesWithBenefitsData?.benefits?.benefitEmployeesConnection?.edges) {
      return []
    }

    return allEmployeesWithBenefitsData.benefits.benefitEmployeesConnection.edges
      .map((edge) => edge.node.employee?.id)
      .filter((id): id is string => id != null)
  }, [allEmployeesWithBenefitsData])

  const handleActivationResult: OnActivationResultFn = (data) => {
    setActivationData(data)
    setSidepanelState(true)
    setShowWizardModal(false)
  }

  const handleModalClose = () => {
    setShowWizardModal(false)
  }

  useEffect(() => {
    track(trackingEvents.benefits.openBenefitsEmployeesPage)
  })

  useEffect(() => {
    return () => {
      setShowWizardModal(false)
      setSidepanelState(false)
    }
  }, [])

  useEffect(() => {
    const fetchLegalEntities = async () => {
      try {
        const { data } = await client.query({
          query: BenefitsEmployeesFilterCompanyLegalEntitiesDocument,
          variables: {
            companyId,
          },
          fetchPolicy: 'cache-first',
        })

        const entities = data.apiCore?.company?.legalEntities || []
        setLegalEntities(entities)
      } catch (error) {
        setLegalEntities([])
      }
    }

    fetchLegalEntities()
  }, [client, companyId])

  const dataSourceOptions = useMemo(() => {
    const entityPresets =
      legalEntities.length > 1
        ? legalEntities.slice(0, 3).map((entity) => ({
            label: entity.legalName,
            filter: {
              legalEntity: [entity.id],
            },
          }))
        : []

    const allLegalEntitiesPreset =
      legalEntities.length > 1
        ? {
            label: i18n.t('benefits.tabs.employees.page.table.filters.all_legal_entities'),
            filter: {
              legalEntity: [],
            },
          }
        : null

    // Only include presets if we have any
    const presets = [...entityPresets]
    if (allLegalEntitiesPreset) {
      presets.push(allLegalEntitiesPreset)
    }

    return {
      document: BenefitsEmployeesPageDataCollectionDocument,
      mapData: (data: BenefitsEmployeesPageDataCollectionQuery) => {
        return data.benefits.benefitEmployeesConnection.edges.map((edge) => edge.node)
      },

      mapPagination: (data: BenefitsEmployeesPageDataCollectionQuery) => {
        const count = data.benefits.benefitEmployeesConnection.totalCount ?? 0
        return { total: count }
      },

      mapVariables: ({
        filters,
        search,
      }: {
        filters: {
          team?: string[]
          legalEntity?: string[]
          manager?: string[]
        }
        search?: string
      }) => {
        return {
          search,
          teamIds: filters.team?.length ? filters.team : undefined,
          legalEntityIds: filters.legalEntity?.length ? filters.legalEntity : undefined,
          managerIds: filters.manager?.length ? filters.manager : undefined,
        }
      },

      search: {
        enabled: true,
      },

      filters: {
        team: {
          type: 'in' as const,
          label: i18n.t('benefits.tabs.employees.page.table.filters.team'),
          options: {
            options: async () => {
              const { data } = await client.query({
                query: BenefitsEmployeesFilterTeamNamesDocument,
                fetchPolicy: 'cache-first',
              })

              return data.teams.teamsConnection.edges.map((edge) => ({
                label: edge.node.name,
                value: edge.node.id,
              }))
            },
          },
        },
        legalEntity: {
          type: 'in' as const,
          label: i18n.t('benefits.tabs.employees.page.table.filters.legal_entity'),
          options: {
            options: async () => {
              const { data } = await client.query({
                query: BenefitsEmployeesFilterCompanyLegalEntitiesDocument,
                variables: {
                  companyId,
                },
                fetchPolicy: 'cache-first',
              })

              return (
                data.apiCore?.company?.legalEntities?.map((legalEntity) => ({
                  label: legalEntity.legalName,
                  value: legalEntity.id,
                })) || []
              )
            },
          },
        },
        manager: {
          type: 'in' as const,
          label: i18n.t('benefits.tabs.employees.page.table.filters.manager'),
          options: {
            options: async () => {
              const { data } = await client.query({
                query: BenefitsEmployeesFilterManagersDocument,
                fetchPolicy: 'cache-first',
              })

              return data.employees.employeeNamesConnection.nodes.map((node) => ({
                label: node.fullName ?? '',
                value: node.id,
              }))
            },
          },
        },
      },
      primaryActions: () => ({
        label: i18n.t('benefits.tabs.employees.primary_action'),
        onClick: () => setShowWizardModal(true),
        icon: Add,
      }),
      presets: presets.length > 0 ? presets : undefined,
    }
  }, [client, legalEntities, companyId])

  const source = useGraphqlDataCollectionSource(dataSourceOptions, [])

  return (
    <ResponsivePage.Body>
      <Box gap="s32">
        <Box gap="s8">
          <Title
            variant="400"
            title={i18n.t('benefits.tabs.employees.page.title')}
            description={i18n.t('benefits.tabs.employees.page.description')}
          />
        </Box>

        {activationData && (
          <CalloutResults
            activationData={activationData}
            onReviewClick={() => setSidepanelState(true)}
          />
        )}

        <OneDataCollection
          source={source}
          visualizations={[
            {
              type: 'table',
              options: {
                columns: [
                  {
                    label: i18n.t('benefits.tabs.employees.page.table.columns.employee'),
                    render: (item) => {
                      if (!item.employee) return { type: 'text', value: '' }

                      return {
                        type: 'person',
                        value: {
                          firstName: item.employee.firstName,
                          lastName: item.employee.lastName,
                          src: buildUrl(item.employee.avatar?.url ?? ''),
                        },
                      }
                    },
                  },
                  {
                    label: i18n.t('benefits.tabs.employees.page.table.columns.team'),
                    render: (item) => {
                      if (!item.employee) return { type: 'text', value: '' }

                      return {
                        type: 'team',
                        value: {
                          name: item.employee.memberships[0]?.team?.name || '',
                          src: buildUrl(item.employee.memberships[0]?.team?.avatar ?? ''),
                        },
                      }
                    },
                  },
                  {
                    label: i18n.t('benefits.tabs.employees.page.table.columns.manager'),
                    render: (item) => {
                      if (!item.employee || !item.employee.manager)
                        return { type: 'text', value: '' }

                      return {
                        type: 'person',
                        value: {
                          firstName: item.employee.manager.firstName,
                          lastName: item.employee.manager.lastName,
                          src: buildUrl(item.employee.manager.avatar?.url ?? ''),
                        },
                      }
                    },
                  },
                  {
                    label: i18n.t('benefits.tabs.employees.page.table.columns.benefits'),
                    render: (item) => {
                      const totalBenefits = item.activeBenefits.length

                      if (totalBenefits === 0) return { type: 'text', value: '' }

                      const translatedBenefits = item.activeBenefits.map((benefit) =>
                        translateBenefitType(benefit)
                      )

                      return {
                        type: 'text',
                        value: translatedBenefits.join(', '),
                      }
                    },
                  },
                ],
                frozenColumns: 1,
              },
            },
          ]}
        />
      </Box>

      <ActivationResultSidePanel
        open={sidepanelState}
        onClose={() => setSidepanelState(false)}
        activationData={activationData}
      />

      {showWizardModal && (
        <ActivateBenefitsModal
          key="activate-benefits-modal"
          onClose={handleModalClose}
          employeeIdsWithBenefits={employeesWithBenefits}
          onActivationResult={handleActivationResult}
        />
      )}
    </ResponsivePage.Body>
  )
}

export default EmployeesPage
